/**
 * Dashboard API Service
 * 
 * TypeScript service layer for fetching dashboard metrics from Lambda API
 */

// API Base URL now handled in ../config.ts
import { API_BASE_URL } from '../config';

// ============================================
// Type Definitions
// ============================================

export interface OverallMetrics {
    geo: string;
    total_subscribers: number;
    active_subscribers: number;
    active_campaigns_30d: number;
    overall_open_rate: number;
    overall_click_rate: number;
    latest_campaign: {
        id: string;
        name: string;
        send_date: string;
        open_rate: number;
    } | null;
    last_updated: string;
}

export interface EspMetric {
    esp_family: string;
    subscriber_count: number;
    avg_open_rate: number;
    avg_click_rate: number;
    total_sends: number;
    total_opens: number;
    reputation_score: number;
    trend: 'improving' | 'stable' | 'declining';
}

export interface EspPerformanceResponse {
    geo: string;
    period_days: number;
    esp_metrics: EspMetric[];
}

export interface TagMetric {
    tag: string;
    count: number;
    percentage: number;
    avg_open_rate: number;
    avg_click_rate: number;
    avg_days_subscribed: number;
}

export interface TagDistributionResponse {
    geo: string;
    total_subscribers: number;
    tag_metrics: TagMetric[];
}

export interface Campaign {
    campaign_id: string;
    newsletter_id: string;
    campaign_name: string;
    send_date: string;
    subject_line: string;
    total_sent: number;
    total_delivered: number;
    total_bounced: number;
    unique_opens: number;
    unique_clicks: number;
    delivery_rate: number;
    open_rate: number;
    click_rate: number;
    bounce_rate: number;
    esp_breakdown: Record<string, {
        sent: number;
        opened?: number;
        open_rate: number;
        click_rate: number;
        estimated_open_rate?: number;
    }>;
}

export interface CampaignHistoryResponse {
    geo: string;
    esp: string;
    campaign_count: number;
    campaigns: Campaign[];
}

export interface GrowthDataPoint {
    date: string;
    new_subscribers: number;
    cumulative_total: number;
}

export interface SubscriberGrowthResponse {
    geo: string;
    period: string;
    data_points: GrowthDataPoint[];
    summary: {
        total_new_subscribers: number;
        growth_rate_pct: number;
        current_total: number;
    };
}

export interface EspConfig {
    id: string;
    label: string;
    esp_provider: string;
    geo: string;
    is_active: boolean;
    list_id: string;
    has_api_key?: boolean;
    subid1?: string;
    stats?: {
        fresh_recruits: number;
        new_added_7d?: number;
        new_openers_7d?: number;
        inactive_left?: number;
        last_upload: {
            size: number;
            status: string;
            date: string;
        } | null;
        error?: string;
    };
    global_stats?: {
        total_active: number;
        uploaded_today: number;
        error?: string;
    };
    simplifiedMode?: boolean;
    suspendAfterDays?: number;
    deleteAfterDays?: number;
    dailyIntakeMin?: number;
    dailyIntakeMax?: number;
    accountCap?: number;
    priorityTags?: string[];
    prioritySuppliers?: string[];
}

export interface ToggleEspResponse {
    success: boolean;
    id: string;
    is_active: boolean;
}

export interface WctDashboardResponse {
    kpis: {
        total_conversions: number;
        total_revenue: number;
        approved_conversions: number;
    };
    advertiser_report: {
        advertiser: string;
        campaign_name: string;
        geo: string;
        conversions: number;
        commission: number;
    }[];
    chart_data: {
        date: string;
        status: string;
        commission: number;
        conversions: number;
    }[];
    latest_data_date?: string | null;
    actual_days?: number;
    auto_expanded?: boolean;
    today?: { conversions: number; commission: number };
    yesterday?: { conversions: number; commission: number };
}

export interface WctTransaction {
    order_date: string;
    transaction_id: string;
    advertiser: string;
    campaign_name: string;
    esp: string;
    sub_id_1: string;
    sub_id_2: string;
    sub_id_3?: string;
    sub_id_4?: string;
    sub_id_5?: string;
    status: string;
    commission: number;
}

export interface ConversionParityRow {
    network_id: string;
    account?: string | null;
    has_api_sync?: boolean;
    date?: string;
    matched: number;
    wct_only: number;
    api_only: number;
    wct_revenue_dkk: number;
    api_revenue_dkk: number;
    matched_revenue_abs_diff_dkk: number;
    status_mismatch: number;
    last_api_seen: string | null;
    coverage_pct: number | null;
}

export interface ConversionParityResponse {
    days: number;
    networks: string[];
    summary: ConversionParityRow[];
    daily: ConversionParityRow[];
    unmatched_samples: {
        network_id: string; account: string | null; transaction_id: string; advertiser_name: string;
        timestamp: string; side: 'wct_only' | 'api_only'; wct_revenue_dkk: number; api_revenue_dkk: number;
        status: string; api_status: string | null;
    }[];
}

export interface ApplicationsRow {
    key?: string;
    label?: string;
    network_id?: string;
    networks?: number;
    applications: number;
    offers: number;
    paid: number;
    rejected: number;
    open: number;
    expired: number;
    revenue_dkk: number | null;
    median_hours_to_paid: number | null;
    score_a: number; score_b: number; score_c: number; score_d: number; score_e: number; score_f: number;
    scored: number;
    paid_pct: number | null;
    dkk_per_application: number | null;
    dkk_per_paid: number | null;
}

export interface ApplicationsResponse {
    days: number;
    group_by: string;
    rows: ApplicationsRow[];
    totals: ApplicationsRow;
    salus_scores: { score: string; applications: number; paid: number; revenue_dkk: number | null; paid_pct: number | null; dkk_per_application: number | null; dkk_per_paid: number | null }[];
    daily: { date: string; applications: number; paid: number; rejected: number; revenue_dkk: number | null }[];
    last_updated: string | null;
}

export interface MoneyTransactionRow {
    timestamp: string;
    network_id: string;
    account: string | null;
    advertiser_name: string;
    transaction_id: string;
    status: string;
    revenue_dkk: number;
    sub1: string | null; sub2: string | null; sub3: string | null; sub4: string | null;
    tier: string | null;
    score: string | null;
    application_stage: string | null;
    application_applied_at: string | null;
    source: string;
}

export interface MoneyTransactionsResponse {
    days: number;
    kpis: { transactions: number; revenue_dkk: number | null; pending: number; pending_dkk: number | null; with_application: number };
    by_tier: { network_id: string; tier: string; transactions: number; revenue_dkk: number; avg_dkk: number }[];
    by_partner: { network_id: string; advertiser_name: string; transactions: number; revenue_dkk: number; avg_dkk: number }[];
    rows: MoneyTransactionRow[];
}

export interface WctMissingTransaction {
    order_date: string;
    transaction_id: string;
    advertiser: string;
    campaign_name: string;
    geo: string;
    commission: number;
    network_id: string | null;
    pubidsub1: string | null;
    pubidsub2: string | null;
    raw_sub1: string | null;
    raw_sub2: string | null;
    raw_sub3: string | null;
    raw_sub4: string | null;
    raw_sub5: string | null;
    click_id: string | null;
}

export interface UnmatchedConversion {
    id: number;
    conversion_id: string;
    transaction_id: string;
    advertiser_name: string;
    network_id: string;
    geo: string;
    category: string;
    revenue: number;
    pubidsub1: string;
    pubidsub2: string;
    pubidsub3: string;
    pubidsub4: string;
    pubidsub5: string;
    status: string;
    created_at: string;
}

export interface WctMissingMapping {
    advertiser_name: string;
    conversion_count: number;
}

export interface UpsertMappingParams {
    advertiser_name: string;
    geo?: string;
    campaign_name?: string;
    category?: string;
    campaign_id?: string;
}

export interface UpsertTransactionOverrideParams {
    transaction_id: string;
    esp: string;
}

export interface WctCategory {
    id: number;
    name: string;
}

export interface SimplyloanStatsResponse {
    total: number;
    today: number;
    timestamp_column_used: string | null;
    daily_counts: {
        date: string;
        count: number;
    }[];
    recent_subscribers?: Record<string, any>[];
}


export interface IncomingEmailAssessment {
    message_id: string;
    sender: string;
    subject: string;
    intent: string;
    sentiment: string;
    summary: string;
    suggested_action: string;
    status: string;
    is_subscriber: boolean;
    associated_geos: string[];
    received_at: string;
    email_body?: string;
    receiver?: string;
}

export interface IncomingEmailStatsResponse {
    success: boolean;
    kpis: {
        total: number;
        pending: number;
        processed_auto: number;
        processed_manual: number;
        unsubscribes: number;
        complaints: number;
        bounces: number;
        inquiries: number;
        ooo: number;
        notifications: number;
    };
    domains: { receiver: string; count: number }[];
    trend: {
        day: string;
        total: number;
        unsubscribes: number;
        complaints: number;
        bounces: number;
    }[];
}

export interface SegmentCompositionEsp {
    esp_id: string;
    label: string;
    geo: string;
    provider: string;
    active: number;
    warm: number;
    onboarding: number;
    reengagement: number;
    fresh: number;
    send_segment: number;
    total_subs: number;
    openers: number;
    growth_rate: number;
    delta_active: number;
    delta_warm: number;
    delta_onboarding: number;
    delta_reengagement: number;
    delta_fresh: number;
    delta_send_segment: number;
    delta_total_subs: number;
}

export interface SegmentCompositionResponse {
    esps: SegmentCompositionEsp[];
    snapshot_date: string | null;
    prev_date: string | null;
}

export interface IspHealthSignals {
    or_current: number;
    or_7d_avg: number;
    or_trend: 'up' | 'down' | 'stable';
    spam_rate: number;
    bounce_rate: number;
    unsub_rate: number;
    volume_current: number;
    volume_7d_avg: number;
}

export interface IspModeSuggestion {
    esp_id: string;
    esp_label: string;
    isp: string;
    current_mode: 'MAINTENANCE' | 'GROW' | 'REPUTATION';
    suggested_mode: 'MAINTENANCE' | 'GROW' | 'REPUTATION' | null;
    trigger: 'CRISIS' | 'RECOVERY' | 'OPPORTUNITY' | 'FATIGUE' | null;
    reason: string | null;
    signals: IspHealthSignals;
    confidence: 'HIGH' | 'MEDIUM' | 'LOW' | null;
    suggested_at: string | null;
    reputation_day?: number;
    reputation_recency?: number;
}

export interface IspHealthResponse {
    suggestions: IspModeSuggestion[];
    last_evaluated: string | null;
}

// ─── Lead Dashboard Types ────────────────────────────────────

export interface LeadSupplierBreakdown {
    count: number;
    cost: number;
    price_per_lead: number;
    eom_leads: number;
    eom_cost: number;
}

export interface LeadGeoBreakdown {
    geo: string;
    suppliers: Record<string, LeadSupplierBreakdown>;
    total: number;
    total_cost: number;
    eom_leads: number;
    eom_cost: number;
    max_cap: number | null;
}

export interface LeadDashboardResponse {
    kpis: {
        total: number;
        accepted: number;
        rejected: number;
        pending_sync: number;
    };
    geo_breakdown: LeadGeoBreakdown[];
    reject_reasons: Record<string, number>;
    queue_status: Record<string, number>;
    recent_leads: LeadRow[];
    period: { start: string; end: string; days_passed: number; days_in_month: number };
    cost_config: Record<string, Record<string, number>>;
    suppliers_config: Record<string, string[]>;
}

export interface LeadRow {
    id: number;
    email_masked: string;
    first_name: string;
    geo: string;
    supplier: string;
    lead_status: string;
    reject_reason: string;
    original_timestamp: string;
    migrated_at: string;
}

export interface LeadRecentResponse {
    leads: LeadRow[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
}

export interface LeadHygieneBuffer {
    current_buffer: number;
    target_buffer: number;
    pct_of_target: number;
    status: 'healthy' | 'deficit';
}

export interface LeadHygieneKpis {
    total_tested: number;
    deliverable: number;
    spam_traps: number;
    invalids: number;
    disposables: number;
    accept_all: number;
    pass_rate_pct: number;
}

export interface LeadHygieneSupplier {
    supplier: string;
    geo: string;
    total: number;
    deliverable: number;
    spam_traps: number;
    invalids: number;
    pass_rate_pct: number;
}

export interface LeadCleaningLogRow {
    email: string;
    geo: string;
    supplier: string;
    code: string;
    result: string;
    reason: string;
    is_deliverable: boolean;
    created_at: string;
}

export interface DeBounceCreditsStatus {
    balance: number | null;
    status: 'healthy' | 'warning' | 'critical' | 'unknown';
    warning_threshold: number;
    critical_threshold: number;
    is_low: boolean;
    used_24h: number;
    used_7d: number;
    used_30d: number;
    avg_daily_burn_7d: number;
    avg_daily_burn_30d: number;
    estimated_days_remaining: number | null;
    checked_at: string;
}

export interface LeadHygieneResponse {
    timestamp: string;
    credits?: DeBounceCreditsStatus;
    buffers: Record<string, LeadHygieneBuffer>;
    kpis: LeadHygieneKpis;
    suppliers: LeadHygieneSupplier[];
    recent_logs: LeadCleaningLogRow[];
}


// ============================================
// API Client Class
// ============================================

class DashboardApiClient {
    private baseUrl: string;

    constructor(baseUrl: string = API_BASE_URL) {
        this.baseUrl = baseUrl;
    }

    /**
     * Generic fetch wrapper with error handling
     */
    private async fetch<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
        const url = new URL(`${this.baseUrl}${endpoint}`);

        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                url.searchParams.append(key, value);
            });
        }

        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({
                error: 'Unknown error',
                message: response.statusText
            }));
            const msg = error.error || error.message || response.statusText || `API Error: ${response.status}`;
            throw new Error(msg);
        }

        return response.json();
    }

    /**
     * Get overall dashboard metrics
     */
    async getOverallMetrics(geo: string = 'no', esp: string = 'gr'): Promise<OverallMetrics> {
        return this.fetch<OverallMetrics>('/api/metrics/overall', { geo, esp });
    }

    /**
     * Get ESP performance metrics
     */
    async getEspPerformance(
        geo: string = 'no',
        days: number = 30,
        esp: string = 'gr'
    ): Promise<EspPerformanceResponse> {
        return this.fetch<EspPerformanceResponse>('/api/metrics/esp-performance', {
            geo,
            days: String(days),
            esp,
        });
    }

    /**
     * Get tag distribution metrics
     */
    async getTagDistribution(geo: string = 'no', esp: string = 'gr'): Promise<TagDistributionResponse> {
        return this.fetch<TagDistributionResponse>('/api/metrics/tag-distribution', { geo, esp });
    }

    /**
     * Get campaign history
     */
    async getCampaignHistory(
        geo: string = 'no',
        esp: string = 'gr',
        limit: number = 10,
        esp_id?: string
    ): Promise<CampaignHistoryResponse> {
        const params: Record<string, string> = {
            geo,
            esp,
            limit: String(limit),
        };
        if (esp_id) {
            params.esp_id = esp_id;
        }
        return this.fetch<CampaignHistoryResponse>('/api/metrics/campaign-history', params);
    }

    /**
     * Get subscriber growth data
     */
    async getSubscriberGrowth(
        geo: string = 'no',
        period: '7d' | '30d' | '90d' | '1y' = '30d'
    ): Promise<SubscriberGrowthResponse> {
        return this.fetch<SubscriberGrowthResponse>('/api/metrics/subscriber-growth', {
            geo,
            period,
        });
    }

    /**
     * Get all ESP configurations
     */
    async getEsps(): Promise<{ esps: EspConfig[] }> {
        return this.fetch<{ esps: EspConfig[] }>('/api/segmentation/esps');
    }

    /**
     * Toggle ESP active status
     */
    async toggleEsp(id: string, is_active: boolean): Promise<ToggleEspResponse> {
        return this.fetchWithOpts<ToggleEspResponse>('/api/segmentation/esps/toggle', {
            method: 'POST',
            body: JSON.stringify({ id, is_active })
        });
    }

    /**
     * Set ESP Credentials
     */
    async setEspCredentials(id: string, api_key: string, action: 'set' | 'delete' = 'set'): Promise<{ success: boolean, message: string }> {
        const url = new URL(`${this.baseUrl}/api/segmentation/esps/credentials`);

        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ id, api_key, action })
        });

        if (!response.ok) {
            throw new Error(`API Error: ${response.status}`);
        }

        return response.json();
    }

    /**
     * Update ESP Configuration (List ID and SubID1)
     */
    async updateEspConfig(espId: string, listId: string, subid1: string): Promise<{ success: boolean, message: string }> {
        const url = new URL(`${this.baseUrl}/api/segmentation/esps/config`);

        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ esp_id: espId, list_id: listId, subid1: subid1 })
        });

        if (!response.ok) {
            throw new Error(`API Error: ${response.status}`);
        }

        return response.json();
    }

    /**
     * Update simplified mode config
     */
    async updateSimplifiedConfig(espId: string, config: {
        simplifiedMode?: boolean;
        suspendAfterDays?: number;
        deleteAfterDays?: number;
        dailyIntakeMin?: number;
        dailyIntakeMax?: number;
        accountCap?: number;
        priorityTags?: string[];
        prioritySuppliers?: string[];
    }): Promise<any> {
        const response = await fetch(`${this.baseUrl}/api/segmentation/esps/config`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ esp_id: espId, ...config }),
        });
        if (!response.ok) {
            throw new Error(`API Error: ${response.status}`);
        }
        return response.json();
    }

    // Priority queue operations
    async addToPriorityQueue(espId: string, emails: string[], priorityTag: string = 'manual'): Promise<any> {
        const response = await fetch(`${this.baseUrl}/api/segmentation/esps/priority-queue`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ esp_id: espId, action: 'add', emails, priority_tag: priorityTag, source: 'csv_upload' }),
        });
        if (!response.ok) {
            throw new Error(`API Error: ${response.status}`);
        }
        return response.json();
    }

    async getPriorityQueueStatus(espId: string): Promise<any> {
        const response = await fetch(`${this.baseUrl}/api/segmentation/esps/priority-queue`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ esp_id: espId, action: 'status' }),
        });
        if (!response.ok) {
            throw new Error(`API Error: ${response.status}`);
        }
        return response.json();
    }

    async clearPriorityQueue(espId: string, clearAll: boolean = false): Promise<any> {
        const response = await fetch(`${this.baseUrl}/api/segmentation/esps/priority-queue`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ esp_id: espId, action: 'clear', clear_all: clearAll }),
        });
        if (!response.ok) {
            throw new Error(`API Error: ${response.status}`);
        }
        return response.json();
    }

    /**
     * WCT Dashboard: Get fully mapped conversions
     */
    async getWctDashboard(geo: string = 'all', esp: string = 'all', days: number = 7, showPending: boolean = false, startDate?: string, endDate?: string): Promise<WctDashboardResponse> {
        const params: Record<string, string> = { geo, esp, days: String(days), show_pending: String(showPending) };
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        return this.fetch<WctDashboardResponse>('/api/metrics/wct_dashboard', params);
    }

    /**
     * API Dashboard: Get fully mapped conversions from direct API sync
     */
    async getApiDashboard(geo: string = 'all', esp: string = 'all', days: number = 7, showPending: boolean = false, startDate?: string, endDate?: string): Promise<WctDashboardResponse> {
        const params: Record<string, string> = { geo, esp, days: String(days), show_pending: String(showPending) };
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        return this.fetch<WctDashboardResponse>('/api/metrics/api_dashboard', params);
    }

    /**
     * WCT Dashboard: Get raw transactions
     */
    async getWctTransactions(geo: string = 'all', esp: string = 'all', limit: number = 500, showPending: boolean = false, days: number = 30, startDate?: string, endDate?: string): Promise<{ data: WctTransaction[] }> {
        const params: Record<string, string> = { geo, esp, limit: String(limit), show_pending: String(showPending), days: String(days) };
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        return this.fetch<{ data: WctTransaction[] }>('/api/metrics/wct_transactions', params);
    }

    /**
     * API Dashboard: Get raw transactions from direct API sync
     */
    async getApiTransactions(geo: string = 'all', esp: string = 'all', limit: number = 500, showPending: boolean = false, days: number = 30, startDate?: string, endDate?: string): Promise<{ data: WctTransaction[] }> {
        const params: Record<string, string> = { geo, esp, limit: String(limit), show_pending: String(showPending), days: String(days) };
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        return this.fetch<{ data: WctTransaction[] }>('/api/metrics/api_transactions', params);
    }

    /**
     * WCT phase-out: parity between WeCanTrack and direct network APIs
     */
    async getConversionParity(days: number = 14, network?: string): Promise<ConversionParityResponse> {
        const params: Record<string, string> = { days: String(days) };
        if (network) params.network = network;
        return this.fetch<ConversionParityResponse>('/api/metrics/conversion_parity', params);
    }

    /**
     * Finance funnel: one row per loan application (Salus v3 + score, Axo, Sortter)
     */
    async getApplications(days: number = 30, groupBy: string = 'partner', geo?: string, esp?: string, network?: string): Promise<ApplicationsResponse> {
        const params: Record<string, string> = { days: String(days), group_by: groupBy, geo: geo || 'all', esp: esp || 'all' };
        if (network && network !== 'all') params.network = network;
        return this.fetch<ApplicationsResponse>('/api/metrics/applications', params);
    }

    /**
     * Only conversions that pay money, with tier (Salus score, Axo T1-T5, CAKE payout band)
     */
    async getMoneyTransactions(days: number = 30, geo?: string, esp?: string, tier?: string, network?: string, limit: number = 500): Promise<MoneyTransactionsResponse> {
        const params: Record<string, string> = { days: String(days), geo: geo || 'all', esp: esp || 'all', limit: String(limit) };
        if (tier && tier !== 'all') params.tier = tier;
        if (network && network !== 'all') params.network = network;
        return this.fetch<MoneyTransactionsResponse>('/api/metrics/money_transactions', params);
    }

    /**
     * WCT Dashboard: Get missing mappings
     */
    async getWctMissingMappings(): Promise<{ missing: WctMissingMapping[] }> {
        return this.fetch<{ missing: WctMissingMapping[] }>('/api/metrics/wct_missing_mappings');
    }

    /**
     * API Dashboard: Get missing mappings from direct API sync
     */
    async getApiMissingMappings(): Promise<{ missing: WctMissingMapping[] }> {
        return this.fetch<{ missing: WctMissingMapping[] }>('/api/metrics/api_missing_mappings');
    }

    /**
     * WCT Dashboard: Get missing transactions (campaign found but esp empty/invalid)
     */
    async getWctMissingTransactions(): Promise<{ missing_transactions: WctMissingTransaction[] }> {
        return this.fetch<{ missing_transactions: WctMissingTransaction[] }>('/api/metrics/wct_missing_transactions');
    }

    /**
     * API Dashboard: Get missing transactions from direct API sync
     */
    async getApiMissingTransactions(): Promise<{ missing_transactions: WctMissingTransaction[] }> {
        return this.fetch<{ missing_transactions: WctMissingTransaction[] }>('/api/metrics/api_missing_transactions');
    }

    /**
     * WCT Dashboard: Upsert a mapping for an advertiser
     */
    async upsertWctMapping(mapping: UpsertMappingParams): Promise<{ status: string }> {
        const url = new URL(`${this.baseUrl}/api/metrics/wct_mapping`);
        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(mapping)
        });
        if (!response.ok) throw new Error(`API Error: ${response.status}`);
        return response.json();
    }

    /**
     * WCT Dashboard: Upsert an ESP override for a transaction
     */
    async upsertTransactionOverride(override: UpsertTransactionOverrideParams): Promise<{ status: string }> {
        const url = new URL(`${this.baseUrl}/api/metrics/wct_transaction_override`);
        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(override)
        });
        if (!response.ok) throw new Error(`API Error: ${response.status}`);
        return response.json();
    }

    /**
     * WCT Dashboard: Get all categories
     */
    async getWctCategories(): Promise<{ categories: WctCategory[] }> {
        return this.fetch<{ categories: WctCategory[] }>('/api/metrics/wct_categories');
    }

    /**
     * WCT Dashboard: Get unmatched conversions (no lead found)
     */
    async getUnmatchedConversions(): Promise<{ unmatched: UnmatchedConversion[] }> {
        return this.fetch<{ unmatched: UnmatchedConversion[] }>('/api/metrics/unmatched_conversions');
    }

    /**
     * WCT Dashboard: Assign unmatched conversion to a lead
     */
    async assignUnmatched(id: number, leadId: string): Promise<{ status?: string; error?: string }> {
        const url = new URL(`${this.baseUrl}/api/metrics/unmatched_conversions`);
        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'assign', id, lead_id: leadId })
        });
        if (!response.ok) throw new Error(`API Error: ${response.status}`);
        return response.json();
    }

    /**
     * WCT Dashboard: Dismiss unmatched conversion
     */
    async dismissUnmatched(id: number): Promise<{ status?: string; error?: string }> {
        const url = new URL(`${this.baseUrl}/api/metrics/unmatched_conversions`);
        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'dismiss', id })
        });
        if (!response.ok) throw new Error(`API Error: ${response.status}`);
        return response.json();
    }

    /**
     * WCT Dashboard: Create a Category
     */
    async createWctCategory(name: string): Promise<{ status: string }> {
        const url = new URL(`${this.baseUrl}/api/metrics/wct_categories`);
        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name })
        });
        if (!response.ok) throw new Error(`API Error: ${response.status}`);
        return response.json();
    }

    /**
     * Trigger an operational task (e.g., download_segments, sync_engagement)
     */
    async triggerOp(action: string, params: Record<string, any> = {}): Promise<any> {
        return this.fetchWithOpts<any>('/api/ops/trigger', {
            method: 'POST',
            body: JSON.stringify({ action, ...params })
        });
    }

    /**
     * Trigger sync active subscribers
     */
    async syncActiveSubscribers(geo: string, espId?: string): Promise<any> {
        return this.triggerOp('sync_active_subscribers', {
            geo,
            esp_id: espId
        });
    }

    /**
     * Get Webhook Campaign Stats (opens/clicks per ESP)
     */
    async getWebhookCampaignStats(geo: string = 'all', days: number = 30): Promise<{
        campaigns: {
            campaign_id: string;
            campaign_name: string;
            send_date: string;
            total_sent: number;
            geo: string;
            esp_stats: Record<string, {
                sent: number;
                opens: number;
                clicks: number;
                open_rate: number;
                click_rate: number;
            }>;
        }[];
    }> {
        return this.fetch('/api/metrics/webhook-campaign-stats', { geo, days: String(days) });
    }

    /**
     * Get Simplyloan Subscriber Stats
     */
    async getSimplyloanStats(): Promise<SimplyloanStatsResponse> {
        return this.fetch<SimplyloanStatsResponse>('/api/simplyloan/stats');
    }

    /**
     * SimplyLoan email overview: market status, recent sends, comebacks and audience details
     */
    async getSimplyloanOverview(): Promise<any> {
        const raw = await this.fetch<any>('/api/simplyloan/segmentation/overview');
        return raw.body ? (typeof raw.body === 'string' ? JSON.parse(raw.body) : raw.body) : raw;
    }

    /**
     * Deliverability: DMARC pass rates per sending domain and complaint/bounce/unsubscribe rates per ESP
     */
    async getDeliverabilitySummary(days: number = 30): Promise<any> {
        return this.fetch<any>('/api/deliverability/summary', { days: String(days) });
    }

    /**
     * Get SendSegment composition per ESP (with day-over-day deltas)
     */
    async getSegmentComposition(): Promise<SegmentCompositionResponse> {
        return this.fetch<SegmentCompositionResponse>('/api/analytics/segment-composition');
    }

    /**
     * Get pending incoming emails with optional filters
     */
    async getIncomingEmails(limit: number = 50, offset: number = 0, status?: string, intent?: string): Promise<IncomingEmailAssessment[]> {
        const params: Record<string, string> = { limit: String(limit), offset: String(offset) };
        if (status && status !== 'ALL') params.status = status;
        if (intent && intent !== 'ALL') params.intent = intent;
        return this.fetch<IncomingEmailAssessment[]>('/api/emails/incoming', params);
    }

    /**
     * Get total count of incoming emails with optional filters
     */
    async getIncomingEmailsCount(status?: string, intent?: string): Promise<{ total: number }> {
        const params: Record<string, string> = {};
        if (status && status !== 'ALL') params.status = status;
        if (intent && intent !== 'ALL') params.intent = intent;
        return this.fetch<{ total: number }>('/api/emails/incoming/count', params);
    }

    /**
     * Get aggregated incoming email statistics (KPIs, domains, 7-day trend)
     */
    async getIncomingEmailStats(): Promise<IncomingEmailStatsResponse> {
        return this.fetch<IncomingEmailStatsResponse>('/api/emails/incoming/stats');
    }

    /**
     * Trigger auto-triage of backlogged pending emails
     */
    async autoTriagePendingBacklog(batchSize: number = 200): Promise<{ success: boolean; processed: number; stats?: any; error?: string }> {
        return this.fetchWithOpts<{ success: boolean; processed: number; stats?: any; error?: string }>('/api/emails/incoming/auto_triage_pending', {
            method: 'POST',
            body: JSON.stringify({ batch_size: batchSize })
        });
    }

    /**
     * Process an action on one or more incoming emails (e.g. blacklist, ignore)
     */
    async processIncomingEmailAction(message_id: string | string[], action: string): Promise<{ success: boolean; message?: string }> {
        // Normalize to array for the new bulk API endpoint format
        const message_ids = Array.isArray(message_id) ? message_id : [message_id];

        return this.fetchWithOpts<{ success: boolean; message?: string }>('/api/emails/incoming', {
            method: 'POST',
            body: JSON.stringify({ message_ids, action })
        });
    }

    /**
     * Process a mixed array of actions for multiple emails
     * Example: [{ message_id: "1", action: "blacklist" }, { message_id: "2", action: "ignore" }]
     */
    async autoProcessIncomingEmails(actions: { message_id: string; action: string }[]): Promise<{ success: boolean; processed_stats: any }> {
        return this.fetchWithOpts<{ success: boolean; processed_stats: any }>('/api/emails/incoming/bulk', {
            method: 'POST',
            body: JSON.stringify({ actions })
        });
    }

    /**
     * Manually process a raw array of email addresses to unsubscribe
     */
    async processManualUnsubscribeQueue(emails: string[]): Promise<{ success: boolean; processed_stats: any }> {
        return this.fetchWithOpts<{ success: boolean; processed_stats: any }>('/api/emails/incoming/manual_unsubscribe', {
            method: 'POST',
            body: JSON.stringify({ emails })
        });
    }

    /**
     * Helper to perform fetch with custom options (needed for POST)
     */
    private async fetchWithOpts<T>(endpoint: string, options: RequestInit): Promise<T> {
        const url = new URL(`${this.baseUrl}${endpoint}`);

        const response = await fetch(url.toString(), {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({
                error: 'Unknown error',
                message: response.statusText
            }));
            const msg = error.error || error.message || response.statusText || `API Error: ${response.status}`;
            throw new Error(msg);
        }

        return response.json();
    }

    async updateEspGrowthRate(espId: string, growthRate: number): Promise<{ success: boolean; message: string }> {
        const response = await fetch(`${API_BASE_URL}/api/segmentation/esps/config`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ esp_id: espId, growth_rate: growthRate }),
        });
        if (!response.ok) {
            throw new Error(`API Error: ${response.status}`);
        }
        return response.json();
    }

    /**
     * Get ISP health signals and mode suggestions
     */
    async getIspHealth(espId?: string): Promise<IspHealthResponse> {
        const params: Record<string, string> = {};
        if (espId) params.esp_id = espId;
        return this.fetch<IspHealthResponse>('/api/segmentation/isp-health', params);
    }

    /**
     * Apply a mode change to an ISP
     */
    async applyModeChange(espId: string, isp: string, newMode: string): Promise<{ success: boolean; message: string }> {
        return this.fetchWithOpts<{ success: boolean; message: string }>('/api/segmentation/isp-mode', {
            method: 'POST',
            body: JSON.stringify({ esp_id: espId, isp, mode: newMode })
        });
    }

    /**
     * Dismiss a mode suggestion
     */
    async dismissSuggestion(espId: string, isp: string): Promise<{ success: boolean }> {
        return this.fetchWithOpts<{ success: boolean }>('/api/segmentation/isp-mode/dismiss', {
            method: 'POST',
            body: JSON.stringify({ esp_id: espId, isp })
        });
    }

    async getSegmentationConfig(geo: string, espId: string): Promise<any> {
        return this.fetchWithOpts<any>(`/api/segmentation/config?geo=${geo}&esp_id=${espId}`, { method: 'GET' });
    }

    async saveSegmentationConfig(geo: string, espId: string, config: any): Promise<{ success: boolean; message: string }> {
        return this.fetchWithOpts<{ success: boolean; message: string }>('/api/segmentation/save-config', {
            method: 'POST',
            body: JSON.stringify({ geo, esp_id: espId, config })
        });
    }

    // ─── Lead Dashboard API ──────────────────────────────────────

    async getLeadDashboard(startDate?: string, endDate?: string, geo?: string): Promise<LeadDashboardResponse> {
        const params: Record<string, string> = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        if (geo) params.geo = geo;
        return this.fetch<LeadDashboardResponse>('/api/leads/dashboard', params);
    }

    async getLeadRecent(page: number = 1, perPage: number = 50, filters?: {
        geo?: string; supplier?: string; status?: string; start_date?: string; end_date?: string;
    }): Promise<LeadRecentResponse> {
        const params: Record<string, string> = { page: String(page), per_page: String(perPage) };
        if (filters?.geo) params.geo = filters.geo;
        if (filters?.supplier) params.supplier = filters.supplier;
        if (filters?.status) params.status = filters.status;
        if (filters?.start_date) params.start_date = filters.start_date;
        if (filters?.end_date) params.end_date = filters.end_date;
        return this.fetch<LeadRecentResponse>('/api/leads/recent', params);
    }

    async getLeadHygiene(days: number = 7): Promise<LeadHygieneResponse> {
        const raw: any = await this.fetch<LeadHygieneResponse>('/api/leads/hygiene', { days: String(days) });
        const res: LeadHygieneResponse = raw?.body ? (typeof raw.body === 'string' ? JSON.parse(raw.body) : raw.body) : raw;
        if (res && !res.credits) {
            try {
                const snap = await this.fetch<{ html?: string }>('/api/reports/daily', {
                    date: 'debounce_credits_live_json'
                });
                if (snap && typeof snap.html === 'string') {
                    res.credits = JSON.parse(snap.html);
                }
            } catch {
                // ignore fallback error
            }
        }
        return res;
    }

    async exportLeadCsv(geo: string, password: string): Promise<Response> {
        const url = new URL(`${this.baseUrl}/api/leads/export-csv`);
        url.searchParams.append('geo', geo);
        url.searchParams.append('password', password);
        return fetch(url.toString(), {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });
    }

    async getCampaigns(): Promise<{ campaigns: any[] }> {
        return this.fetch<{ campaigns: any[] }>('/api/text-library/campaigns');
    }

    async getEspKpis(): Promise<any> {
        try {
            return await this.fetch<any>('/api/esp-kpis');
        } catch {
            const fallback = await this.fetch<{ html?: string; error?: string }>('/api/reports/daily', {
                date: 'esp_kpis_live_json'
            });
            if (fallback && typeof fallback.html === 'string') {
                return JSON.parse(fallback.html);
            }
            throw new Error(fallback?.error || 'Could not load ESP KPI data');
        }
    }
}

// ============================================
// Singleton Export
// ============================================

export const dashboardApi = new DashboardApiClient();

// Also export the class for testing
export { DashboardApiClient };
